/**
 * Date cell component for proxy edit panel tables.
 * Handles both display and edit modes for start/end dates.
 */
import React from 'react'
import Datepicker from '../../../../components/common/DatePicker/Datepicker'
import type { EditablePerson } from './ProxyEditPanelUtils'
import { todayMMDDYYYY, addDays, addYears } from './ProxyEditPanelUtils'

interface DateCellProps {
  person: EditablePerson
  field: 'startDate' | 'endDate'
  isEditable: boolean
  isEditing: boolean
  dateError?: string
  onEdit: (personId: string, field: 'startDate' | 'endDate') => void
  onChange: (personId: string, field: 'startDate' | 'endDate', value: string) => void
}

/**
 * Renders a date cell that can be edited inline with a datepicker.
 * Double-click to enter edit mode in editable contexts.
 */
const DateCell: React.FC<DateCellProps> = ({
  person,
  field,
  isEditable,
  isEditing,
  dateError,
  onEdit,
  onChange,
}) => {
  const handleChange = (_name: string, val: string | string[]) => {
    onChange(person.id, field, Array.isArray(val) ? (val[0] ?? '') : val)
  }

  if (isEditing) {
    return (
      <div className="w-36 datepicker-narrow">
        <Datepicker
          name={`${field}-${person.id}`}
          value={person[field] ?? ''}
          openBehaviour="iconView"
          disableBeforeDate={
            field === 'startDate'
              ? todayMMDDYYYY()
              : (person.startDate ?? todayMMDDYYYY())
          }
          disableAfterDate={
            field === 'endDate' && person.startDate
              ? addDays(addYears(person.startDate, 10), -1)
              : ''
          }
          error={field === 'endDate' ? dateError : undefined}
          onChange={handleChange}
        />
      </div>
    )
  }

  return (
    <button
      type="button"
      className={isEditable ? 'p3 cursor-pointer' : 'p3'}
      onDoubleClick={
        isEditable
          ? () => onEdit(person.id, field)
          : undefined
      }
    >
      {person[field] ?? ''}
    </button>
  )
}

export default DateCell
